import { useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { api, formatDate } from "../../api";
import { useApi } from "../../hooks";
import { EmptyState, ErrorState, LoadingState, StatusBadge } from "../Ui";
import { useShowMore } from "../Workflow";
import { fmtCtr, fmtPosition, fmtVolume, sortRows, type Place, type ScResponse } from "../../keywords";
import { SaveBar, SelectAll, SortHeader, toggle, type Sort } from "./shared";

type Site = { siteUrl: string; permissionLevel: string | null };

/** Average position 11 to 30: on page 2 or 3 of Google. */
export const isNearPageOne = (position: number | null | undefined) => position != null && position >= 10.5 && position < 30.5;

export function SearchConsoleTab({ clientId, isAdmin, tags, place }: { clientId: number; isAdmin: boolean; tags: string[]; place: Place }) {
  const [days, setDays] = useState<28 | 90>(28);
  const sc = useApi<ScResponse>(`/api/keywords/${clientId}/search-console?days=${days}`);
  const [text, setText] = useState("");
  // Searches that sit on page 2 or 3 (average position 11 to 30): the cheapest page-one wins.
  const [nearPageOne, setNearPageOne] = useState(false);
  const [sort, setSort] = useState<Sort>({ key: "impressions", dir: "desc" });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState<{ text: string; warn: boolean } | null>(null);
  const [sites, setSites] = useState<Site[] | null>(null);
  const [pick, setPick] = useState("");

  const data = sc.data;
  const rows = data?.rows || [];
  const needle = text.trim().toLowerCase();
  const shown = sortRows(rows.filter((r) => (!needle || r.query.toLowerCase().includes(needle)) && (!nearPageOne || isNearPageOne(r.position))), sort.key, sort.dir);
  const { visible, control } = useShowMore(shown, 100, `${days}|${text}|${nearPageOne}|${sort.key}|${sort.dir}`);

  async function refresh() {
    setBusy(true); setError(""); setNotice(null);
    try { sc.setData(await api<ScResponse>(`/api/keywords/${clientId}/search-console/refresh`, { method: "POST", body: JSON.stringify({ days }) })); }
    catch (e) { setError(e instanceof Error ? e.message : "Could not refresh"); }
    finally { setBusy(false); }
  }
  async function loadSites() {
    setError("");
    try { const r = await api<{ sites: Site[] }>("/api/keywords/search-console/sites"); setSites(r.sites); setPick(data?.site?.site_url || r.sites[0]?.siteUrl || ""); }
    catch (e) { setError(e instanceof Error ? e.message : "Could not list properties"); }
  }
  async function choose() {
    setBusy(true); setError("");
    try {
      const r = await api<ScResponse>(`/api/keywords/${clientId}/search-console/site`, { method: "PUT", body: JSON.stringify({ site_url: pick }) });
      setSites(null);
      if (days === 28) sc.setData(r); else sc.reload();
    } catch (e) { setError(e instanceof Error ? e.message : "Could not save the property"); }
    finally { setBusy(false); }
  }

  if (sc.loading) return <LoadingState label="Reading Search Console" />;
  if (sc.error || !data) return <ErrorState message={sc.error || "Search Console could not be read."} onRetry={sc.reload} />;

  if (!data.scope) return (
    <section className="panel">
      <EmptyState title="Search Console isn't connected yet"
        description="An admin needs to reconnect Google once (Settings → Integrations → Reconnect Google) and allow read-only Search Console access. The Google account used must also have access to this client's property in Search Console."
        action={isAdmin ? <Link className="button secondary" to="/settings#integrations">Open Settings</Link> : undefined} />
    </section>
  );

  const picker = isAdmin && (
    <div className="kw-site-picker">
      {sites === null ? (
        <button type="button" className="text-button" onClick={() => void loadSites()}>{data.site ? "Change property" : "Pick the property"}</button>
      ) : sites.length ? (
        <>
          <select aria-label="Search Console property" value={pick} onChange={(e) => setPick(e.target.value)}>
            {sites.filter((s) => s.permissionLevel !== "siteUnverifiedUser").map((s) => <option key={s.siteUrl} value={s.siteUrl}>{s.siteUrl}</option>)}
          </select>
          <button type="button" className="button secondary" disabled={busy || !pick} onClick={() => void choose()}>Use this property</button>
          <button type="button" className="text-button" onClick={() => setSites(null)}>Cancel</button>
        </>
      ) : <span className="muted">The connected Google account can't see any Search Console properties.</span>}
    </div>
  );

  if (!data.site) return (
    <section className="panel">
      <EmptyState title="No Search Console property for this client" description={data.message || data.error || "Pick the property this client's searches come from."} action={picker || undefined} />
      {error && <p role="alert" className="inline-notice danger">{error}</p>}
    </section>
  );

  const keys = shown.map((r) => r.query);
  return (
    <section className="panel">
      <div className="panel-heading">
        <div>
          <h2>Searches the site shows up for</h2>
          <p>
            {data.site.site_url}{data.site.matched_by === "admin" ? " (picked by an admin)" : ""} · {data.site.last_pulled_at ? `read ${formatDate(data.site.last_pulled_at)}` : "not read yet"} · top 5,000 by impressions
          </p>
        </div>
        <div className="kw-controls">
          <span className="segmented-control compact" role="group" aria-label="Period">
            {([28, 90] as const).map((d) => <button key={d} type="button" className={days === d ? "active" : ""} aria-pressed={days === d} onClick={() => { setDays(d); setSelected(new Set()); }}>{d} days</button>)}
          </span>
          <button type="button" className="button secondary" aria-pressed={nearPageOne} title="Average position 11 to 30" onClick={() => setNearPageOne((v) => !v)}>Page 2 and 3</button>
          <input type="search" placeholder="Filter searches" aria-label="Filter searches" value={text} onChange={(e) => setText(e.target.value)} />
          <button type="button" className="button secondary" disabled={busy} onClick={() => void refresh()}><RefreshCw className={busy ? "spin" : ""} aria-hidden="true" />{busy ? "Reading…" : "Refresh"}</button>
        </div>
      </div>
      {picker}
      {(data.error || error) && <p role="alert" className="inline-notice danger">{error || data.error}</p>}
      {notice && <p role="status" className={`inline-notice ${notice.warn ? "warning" : "success"}`}>{notice.text}</p>}
      {selected.size > 0 && (
        <SaveBar clientId={clientId} keywords={[...selected]} place={place} tags={tags}
          onSaved={(saved, message, warn) => {
            const savedSet = new Set(saved.map((s) => s.toLowerCase()));
            sc.setData((d) => (d ? { ...d, rows: d.rows.map((r) => (savedSet.has(r.query.toLowerCase()) ? { ...r, on_list: true } : r)) } : d));
            setSelected(new Set());
            setNotice({ text: message, warn });
          }} />
      )}
      {rows.length ? (
        <div className="table-wrap">
          <table className="kw-table">
            <thead><tr>
              <th className="kw-check"><SelectAll keys={keys} selected={selected} setSelected={setSelected} /></th>
              <SortHeader label="Search" k="query" sort={sort} setSort={setSort} />
              <SortHeader label="Clicks" k="clicks" sort={sort} setSort={setSort} numeric />
              <SortHeader label="Impressions" k="impressions" sort={sort} setSort={setSort} numeric />
              <SortHeader label="CTR" k="ctr" sort={sort} setSort={setSort} numeric />
              <SortHeader label="Avg position" k="position" sort={sort} setSort={setSort} numeric />
              <th><span className="sr-only">On list</span></th>
            </tr></thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.query} className={selected.has(r.query) ? "selected" : undefined}>
                  <td className="kw-check"><input type="checkbox" aria-label={`Select ${r.query}`} checked={selected.has(r.query)} onChange={() => setSelected(toggle(selected, r.query))} /></td>
                  <td data-label="Search"><strong>{r.query}</strong></td>
                  <td className="number-cell" data-label="Clicks">{fmtVolume(r.clicks)}</td>
                  <td className="number-cell" data-label="Impressions">{fmtVolume(r.impressions)}</td>
                  <td className="number-cell" data-label="CTR">{fmtCtr(r.ctr)}</td>
                  <td className="number-cell" data-label="Avg position">{fmtPosition(r.position)}</td>
                  <td data-label="On list">{r.on_list ? <StatusBadge tone="info">On list</StatusBadge> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="muted kw-empty">Search Console has no searches for this property in the last {days} days.</p>}
      {rows.length > 0 && !shown.length && <p className="muted kw-empty">{nearPageOne && !needle ? "No searches sit on page 2 or 3 right now." : "No searches match this filter."}</p>}
      {control}
    </section>
  );
}
